import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { TestBadge } from "@/components/TestBadge";
import { STATUS_META, COMMITTEES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Newspaper, Palette, Filter, PlusCircle, Inbox } from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";

export default function MySubmissions() {
  const { user } = useAuth();
  const isAdmin = ["comms_admin", "technology", "comms_team", "pr_chairman", "president", "vice_president", "leadership"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [committee, setCommittee] = useState("all");

  useEffect(() => {
    if (params.get("filter") === "revisions") setStatus("additional_info_required");
  }, [params]);

  const load = useCallback(async () => {
    setLoading(true);
    const query = {};
    if (status !== "all") query.status = status;
    if (type !== "all") query.submission_type = type;
    if (committee !== "all") query.committee = committee;
    try {
      const { data } = await api.get("/submissions", { params: query });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [status, type, committee]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((s) => {
    const t = (s.data?.title || s.data?.project_purpose || "").toLowerCase();
    return t.includes(q.toLowerCase());
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">{isAdmin ? "All Requests" : "My Submissions"}</h1>
          <p className="text-sm text-muted-foreground">{isAdmin ? "Every submission across the organization." : "Track everything you've submitted."}</p>
        </div>
        {!isAdmin && (
          <Button asChild className="rounded-full" data-testid="new-submission-btn">
            <Link to="/submit/sunday-news"><PlusCircle className="mr-1.5 h-4 w-4" /> New Submission</Link>
          </Button>
        )}
      </div>

      <Card className="flex flex-wrap items-center gap-3 p-4">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title…" className="pl-9" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-[180px]" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="sunday_news">Sunday News</SelectItem>
            <SelectItem value="comms_request">Communications</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[190px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {isAdmin && (
          <Select value={committee} onValueChange={setCommittee}>
            <SelectTrigger className="w-[170px]" data-testid="filter-committee"><SelectValue placeholder="Committee" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All committees</SelectItem>
              {COMMITTEES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </Card>

      <Card className="divide-y divide-border p-0" data-testid="submissions-list">
        {loading ? (
          <div className="space-y-3 p-4">{[...Array(4)].map((_, i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-secondary" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-12 text-center text-muted-foreground">
            <Inbox className="h-8 w-8" /><p>No submissions match your filters.</p>
          </div>
        ) : filtered.map((s, i) => (
          <motion.div key={s.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}>
            <Link to={`/submissions/${s.id}`} data-testid={`submission-row-${s.id}`}
              className="flex items-center gap-4 p-4 transition-colors hover:bg-secondary/50">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${s.submission_type === "sunday_news" ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent"}`}>
                {s.submission_type === "sunday_news" ? <Newspaper className="h-5 w-5" /> : <Palette className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{s.data?.title || s.data?.project_purpose || "Untitled"}</p>
                <p className="text-xs text-muted-foreground">
                  {s.committee || "—"} {isAdmin && s.creator_name ? `· ${s.creator_name}` : ""} · {s.created_at ? format(new Date(s.created_at), "MMM d, yyyy") : ""}
                </p>
              </div>
              {s.is_test && <TestBadge />}
              {s.priority === "rush" && <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive">Rush</span>}
              <StatusBadge status={s.status} />
            </Link>
          </motion.div>
        ))}
      </Card>
    </div>
  );
}
